// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { UPLOAD_SOURCES } from "@cld/upload-widget";
import t from "common/i18n";
import { selectCloudName, selectNewTlsDomain } from "root/selectors";
import TabHeader from "root/components/TabHeader";
import TabPanel from "tabs/hocs/TabPanel";
import TabFooter from "root/components/TabFooter";
import ServiceLoginIntro from "root/components/ServiceLoginIntro";
import { clearTabSelectionOnNewBatch } from "../common/utils";
import bindActions, { types as ats } from "./actions";
import {
	selectTabData,
	selectContextItems,
	selectExtInstagramServer,
} from "./selectors";
import getInstagram from "./service";
import ContextItems from "./components/ContextItems";
import SVG_ICONS from "~/assets/icons/icons";

import "./InstagramTab.scss";

const { INSTAGRAM } = UPLOAD_SOURCES;

type Props = {
	cloudName: string,
	actions: { [string]: Function },
	addToUploadQueue: Function,
	contextData: { selected: string[] },
	tabData: {
		cloudName: string,
		currentContext: number,
		userName: string,
		isMultiple: boolean,
		isLoggedIn: boolean,
		batchStartCounter: number,
		listeningForAuth: boolean,
	},
	extServer: ?string,
	route: { location: { pathname: string } },
	newTlsDomain?: boolean
};

export const listenForAuth = (props: Props): Promise<any> => {
	let result;

	if (!props.tabData.isLoggedIn && !props.tabData.listeningForAuth
		&& (!props.route || !~props.route.location.pathname.indexOf("auth"))) {
		const setAuthInfoAction = props.actions[ats.SET_AUTH_INFO];
		const { cloudName } = props.tabData;

		props.actions[ats.INIT_AUTH](cloudName);

		result = getInstagram().setAuthListener(cloudName)
			.then((result) => setAuthInfoAction({ cloudName, token: result.token }));
	}

	return result || Promise.resolve(); // returning for test purposes
};

export class InstagramTab extends Component<Props> {
	static getHeaderTitle = () => <div
		className="f4 f3-ns">{t("instagram.header_title")}</div>;

	componentDidMount() {
		listenForAuth(this.props);
	}

	componentDidUpdate(prevProps: Props) {
		if (!this.props.tabData.isLoggedIn
			&& this.props.tabData.isLoggedIn !== prevProps.tabData.isLoggedIn) {
			listenForAuth(this.props);
		}

		clearTabSelectionOnNewBatch(this.props, prevProps,
			() => this.clearSelection()); // only one context so its fine. if there are more then should add clear-all action !
	}

	onClearSelectedClick = () => {
		this.clearSelection();
	};

	onUploadButtonClick = () => {
		this.props.addToUploadQueue({
			files: this.props.contextData.selected,
		});
	};

	onNoAuthActionClick = () => {
		getInstagram().openAuthWindow({
			server: this.props.extServer,
			cloudName: this.props.cloudName,
			newTlsDomain: this.props.newTlsDomain
		});
	};

	onUserLogout = () => {
		this.props.actions[ats.DO_LOGOUT]();
	};

	clearSelection() {
		this.props.actions[ats.CLEAR_IMAGE_SELECTION](this.props.tabData.currentContext);
	}

	renderTabContent() {
		let content;

		if (this.props.tabData.isLoggedIn) {
			content = <div className="tab-authed flex flex-column">
				<TabHeader
					source={INSTAGRAM}
					userName={this.props.tabData.userName}
					onLogout={this.onUserLogout}
					title={InstagramTab.getHeaderTitle}/>

				<ContextItems isMultiple={this.props.tabData.isMultiple}
							  context={this.props.tabData.currentContext}/>
			</div>;
		} else {
			content = <ServiceLoginIntro key="insta_login_intro"
										 title={t("instagram.no_auth_title")}
										 actionTitle={t("instagram.no_auth_action")}
										 dataTest="connect-to-instagram-btn"
										 btnClass="btn-insta"
										 btnIcon={SVG_ICONS.INSTAGRAM}
										 actionHandler={this.onNoAuthActionClick}/>;
		}

		return content;
	}

	renderFooter() {
		const ctxData = this.props.contextData;
		const selectionCount = ctxData && ctxData.selected && ctxData.selected.length;

		return <TabFooter showSelection
						  selectionCount={selectionCount}
						  clearSelectionHandler={this.onClearSelectedClick}
						  uploadButtonHandler={this.onUploadButtonClick}/>;
	}

	render() {
		return <div
			className={`content-wrap bg-source-bg bg-source-bg-c instagram-block ${this.props.tabData.isLoggedIn ? "user-active" : ""}`}>

			<div className="holder large-thin-menu-holder">
				{this.renderTabContent()}
			</div>

			{this.renderFooter()}
		</div>;
	}
}

export default TabPanel({
	id: INSTAGRAM,
})(connect(
	createStructuredSelector({
		cloudName: selectCloudName,
		tabData: selectTabData,
		contextData: selectContextItems,
		extServer: selectExtInstagramServer,
		newTlsDomain: selectNewTlsDomain
	}),
	(dispatch: Function) => ({
		actions: bindActions(dispatch),
	}),
)(InstagramTab));
